import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { useParams } from 'react-router-dom';

import { documentsApi } from 'domain/documents/api';
import type { DocumentSubtype } from 'domain/documents/schemas';
import { documentCategorizationAllowedSelector } from 'domain/documents/selectors/documentCategorizationAllowedSelector';
import { queryOptions } from 'services/query';

import { documentsKeys } from './keys';

// Assignable sub-type catalog. Reference data that rarely changes within a
// session → long explicit staleTime (the global default is 0). companyId is in
// the key (per-company cache isolation — no cache reset on company switch, see
// domain/CLAUDE.md); the CompanyId header carries it to the backend. companyId is read
// from the route (useParams) at the call site — it's always in the workspace/document path.
export const documentSubtypesQuery = (companyId: string) =>
  queryOptions({
    queryKey: documentsKeys.subtypes(companyId),
    queryFn: () => documentsApi.getSubtypes(),
    staleTime: 60 * 60 * 1000, // 1 hour
    select: (data): DocumentSubtype[] => data.subtypes,
  });

// The assignable sub-type catalog is only relevant where the categorization UI is available
// (Bookkeeper), so the fetch is gated on that selector centrally — every consumer is
// auto-gated (incl. the class-component HOC that cannot pass an arg) and none can forget.
// `extraEnabled` composes an extra condition (e.g. defer until a reclassify menu opens).
export const useDocumentSubtypes = (extraEnabled = true) => {
  const { companyId } = useParams();
  const allowed = useSelector(documentCategorizationAllowedSelector);
  return useQuery({
    ...documentSubtypesQuery(companyId ?? ''),
    enabled: allowed && extraEnabled && Boolean(companyId),
  });
};
